/**
 * useConfigValidation — Real-time friendly validation
 *
 * Wraps the config-validator module and maps JSON-path errors
 * to human-readable messages using step/process names.
 */

import { useState, useCallback } from 'react';

/**
 * Parse a validation error path like "processes[0].steps[1].identifiers"
 * into { processIndex, stepIndex, field } for navigation.
 */
function parsePath(path) {
  const result = { processIndex: null, stepIndex: null, field: null, conditionIndex: null };
  const procMatch = path.match(/processes\[(\d+)\]/);
  if (procMatch) result.processIndex = parseInt(procMatch[1]);
  const stepMatch = path.match(/steps\[(\d+)\]/);
  if (stepMatch) result.stepIndex = parseInt(stepMatch[1]);
  const condMatch = path.match(/conditions\[(\d+)\]/);
  if (condMatch) result.conditionIndex = parseInt(condMatch[1]);

  // Determine the field/section
  if (path.includes('.identifiers')) result.field = 'tracking';
  else if (path.includes('.conditions')) result.field = 'conditions';
  else if (path.includes('.custom_fields')) result.field = 'customFields';
  else if (path.includes('.trigger')) result.field = 'trigger';
  else if (path.includes('.url_pattern')) result.field = 'basics';
  else if (path.includes('.application_name')) result.field = 'basics';
  else if (path.includes('.step_id')) result.field = 'basics';
  else if (path.includes('.label') && result.stepIndex !== null) result.field = 'basics';
  else if (path.includes('.process_id')) result.field = 'process';
  else if (path.includes('.process_name')) result.field = 'process';

  return result;
}

/**
 * Convert a technical validation error into a friendly message.
 */
function friendlyMessage(error, processes) {
  const { path, message } = error;
  const parsed = parsePath(path);

  // Get process/step names for context
  let processName = '';
  let stepName = '';
  if (parsed.processIndex !== null && processes[parsed.processIndex]) {
    processName = processes[parsed.processIndex].process_name || `Process ${parsed.processIndex + 1}`;
    if (parsed.stepIndex !== null && processes[parsed.processIndex].steps[parsed.stepIndex]) {
      stepName = processes[parsed.processIndex].steps[parsed.stepIndex].label || `Step ${parsed.stepIndex + 1}`;
    }
  }

  // Map common error patterns to friendly messages
  if (message.includes('identifiers is required') || message.includes('at least one identifier')) {
    return {
      message: `${stepName || 'This step'} needs at least one tracking value.`,
      action: 'Add one now',
      ...parsed
    };
  }
  if (message.includes('trigger') && message.includes('selector') && message.includes('required')) {
    return {
      message: `${stepName || 'This step'} is missing a click trigger.`,
      action: 'Set it up',
      ...parsed
    };
  }
  if (message.includes('step_id') && message.includes('required')) {
    return {
      message: `${stepName || 'A step'} in ${processName} is missing a Step ID.`,
      action: 'Fix it',
      ...parsed
    };
  }
  if (message.includes('label') && message.includes('required') && parsed.stepIndex !== null) {
    return {
      message: `A step in ${processName} is missing a label.`,
      action: 'Fix it',
      ...parsed
    };
  }
  if (message.includes('process_id') && message.includes('required')) {
    return {
      message: `${processName || 'A process'} is missing a Process ID.`,
      action: 'Fix it',
      ...parsed
    };
  }
  if (message.includes('process_name') && message.includes('required')) {
    return {
      message: `A process is missing a name.`,
      action: 'Fix it',
      ...parsed
    };
  }
  if (message.includes('url_pattern') && message.includes('required')) {
    return {
      message: `${stepName || 'This step'} is missing a URL pattern.`,
      action: 'Fix it',
      ...parsed
    };
  }
  if (message.includes('application_name') && message.includes('required')) {
    return {
      message: `${stepName || 'This step'} is missing an application name.`,
      action: 'Fix it',
      ...parsed
    };
  }
  if (message.includes('identifier') && message.includes('label')) {
    return {
      message: `A tracking value in ${stepName || 'a step'} is missing a label.`,
      action: 'Fix it',
      ...parsed
    };
  }
  if (message.includes('identifier') && message.includes('selector')) {
    return {
      message: `A tracking value in ${stepName || 'a step'} is missing a CSS selector.`,
      action: 'Fix it',
      ...parsed
    };
  }

  // Fallback — use the original message but with context
  const context = stepName ? `${processName} → ${stepName}` : processName || '';
  return {
    message: context ? `${context}: ${message}` : message,
    action: 'Fix it',
    ...parsed
  };
}

export default function useConfigValidation() {
  const [errors, setErrors] = useState([]);

  /**
   * Validate the full config. Returns { valid, errors }.
   * Errors are friendly objects with { message, action, processIndex, stepIndex, field }.
   */
  const validate = useCallback((processes) => {
    if (!processes || processes.length === 0) {
      const errs = [{ message: 'At least one process is required.', action: 'Add one', processIndex: null, stepIndex: null, field: 'process' }];
      setErrors(errs);
      return { valid: false, errors: errs };
    }

    const rawErrors = [];

    // Validate each process
    processes.forEach((p, pi) => {
      const pPath = `processes[${pi}]`;
      if (!p.process_id) rawErrors.push({ path: `${pPath}.process_id`, message: 'process_id is required' });
      if (!p.process_name) rawErrors.push({ path: `${pPath}.process_name`, message: 'process_name is required' });

      if (!p.steps || p.steps.length === 0) {
        rawErrors.push({ path: `${pPath}.steps`, message: 'at least one step is required' });
      }

      (p.steps || []).forEach((s, si) => {
        const sPath = `${pPath}.steps[${si}]`;
        if (!s.step_id) rawErrors.push({ path: `${sPath}.step_id`, message: 'step_id is required' });
        if (!s.label) rawErrors.push({ path: `${sPath}.label`, message: 'label is required' });
        if (!s.application_name) rawErrors.push({ path: `${sPath}.application_name`, message: 'application_name is required' });
        if (!s.url_pattern) rawErrors.push({ path: `${sPath}.url_pattern`, message: 'url_pattern is required' });
        if (!s.trigger_selector) rawErrors.push({ path: `${sPath}.trigger.selector`, message: 'trigger selector is required' });

        // Identifiers
        if (!s.identifiers || s.identifiers.length === 0) {
          rawErrors.push({ path: `${sPath}.identifiers`, message: 'at least one identifier is required' });
        } else {
          s.identifiers.forEach((id, ii) => {
            if (!id.label) rawErrors.push({ path: `${sPath}.identifiers[${ii}].label`, message: 'identifier label is required' });
            if (!id.selector) rawErrors.push({ path: `${sPath}.identifiers[${ii}].selector`, message: 'identifier selector is required' });
          });
        }

        // Conditions (optional but validate if present)
        s.conditions.forEach((c, ci) => {
          if (!c.selector) rawErrors.push({ path: `${sPath}.conditions[${ci}].selector`, message: 'condition selector is required' });
        });
      });
    });

    const friendly = rawErrors.map(e => friendlyMessage(e, processes));
    setErrors(friendly);
    return { valid: rawErrors.length === 0, errors: friendly };
  }, []);

  /**
   * Validate a single wizard step. Returns { valid, errors } for just that step.
   */
  const validateWizardStep = useCallback((stepNumber, processes) => {
    const allResult = validate(processes);
    if (allResult.valid) return { valid: true, errors: [] };

    // Filter errors relevant to this wizard step
    const relevant = allResult.errors.filter(e => {
      switch (stepNumber) {
        case 1: return e.field === 'process';
        case 2: return e.field === 'basics';
        case 3: return e.field === 'trigger';
        case 4: return e.field === 'tracking';
        case 5: return e.field === 'conditions' || e.field === 'customFields';
        default: return true;
      }
    });

    setErrors(relevant);
    return { valid: relevant.length === 0, errors: relevant };
  }, [validate]);

  const clearErrors = useCallback(() => setErrors([]), []);

  return { errors, validate, validateWizardStep, clearErrors };
}
